import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, ChevronDown, ChevronRight, Check, Ban, ArrowLeftRight, Flag, AlertTriangle } from 'lucide-react';
import { useProjectStore } from '../../../store/projectStore';
import { useInventoryStore, cataloguePickerItems } from '../../../store/inventoryStore';
import { useAuthStore } from '../../../store/authStore';
import { hasPendingManagerReview, isOrderAssignment, isReviewAssignment } from '../../../store/stockRequestAssignment';
import {
  useStockRequestStore,
  StockRequest,
  StockRequestLine,
  LineApprovalInput,
  LineOrderInput,
  LineReceiveInput,
  StockRequestEvent,
  fmtRequestDate,
  formatRequestRef,
  lineNeedsResolution,
  effectiveLineItemId,
  fmtRequestDay,
  deriveRequestStatus,
  isLineReceivable,
  remainingReceiveQty,
} from '../../../store/stockRequestStore';
import { getRequestTheme, statusBadge, fmtStatusLabel, lineStatusBadgeClass, requestStatusBadgeClass, dispositionFieldClass, supplierBadgeClass, supplierBadgeStyle } from './theme';
import NegotiationLinePanel from './NegotiationLinePanel';
import SupplierSelect from './SupplierSelect';
import ExpectedDateInput from './ExpectedDateInput';
import { useSuppliersStore } from '../../../store/suppliersStore';
import type { RequestsSubTab } from './RequestsView';

/** Normal line columns — composed per view (review / receiving / ordering). */
const NORMAL_LINE_COLUMN_DEFS = {
  item: { key: 'item', width: '36%' as const },
  status: { key: 'status', width: 132 as const },
  requested: { key: 'requested', width: 76 as const },
  approved: { key: 'approved', width: 76 as const },
  received: { key: 'received', width: 76 as const },
  actions: { key: 'actions', width: 104 as const },
};

const ORDERING_LINE_COLUMNS = [
  { key: 'include', label: '', width: 44 },
  { key: 'item', label: 'Item', width: '28%' },
  { key: 'qty', label: 'Qty Approved', width: 100 },
  { key: 'po', label: 'PO Number', width: '16%' },
  { key: 'supplier', label: 'Supplier *', width: '12%' },
  { key: 'eta', label: 'Expected Delivery', width: 132 },
  { key: 'ordered', label: 'Ordered', width: 88 },
] as const;

/** Read-only ordering columns — shown in Receiving / after fully ordered (no include / Ordered). */
const ORDERING_VIEW_COLUMN_DEFS = {
  item: { key: 'item', label: 'Item', width: '30%' },
  qty: { key: 'qty', label: 'Qty Approved', width: 100 },
  po: { key: 'po', label: 'PO Number', width: '18%' },
  supplier: { key: 'supplier', label: 'Supplier', width: '12%' },
  eta: { key: 'eta', label: 'Expected Delivery', width: 132 },
} as const;

const ORDERED_REQUEST_STATUSES = new Set(['partially_ordered', 'ordered', 'partially_received', 'completed']);
const LINE_ORDERED_STATUSES = new Set(['ordered', 'partially_received', 'received', 'completed']);

const RECEIVING_EDIT_LINE_COLUMNS = [
  { key: 'include', label: '', width: 44 },
  { key: 'item', label: 'Item', width: '24%' },
  { key: 'qty', label: 'Qty Approved', width: 88 },
  { key: 'po', label: 'PO Number', width: '16%' },
  { key: 'supplier', label: 'Supplier', width: '12%' },
  { key: 'eta', label: 'Expected Delivery', width: 120 },
  { key: 'received', label: 'Received', width: 76 },
  { key: 'receiveNow', label: 'Receive now', width: 88 },
] as const;

interface LineReceiveDraft {
  qty: number;
  units: { serial: string; mac: string }[];
  dispute: boolean;
  disputeReason: string;
  include: boolean;
}

function isEditableReviewLine(line: StockRequestLine): boolean {
  return !['cancelled', 'unavailable'].includes(line.status)
    && line.substituteStatus !== 'accepted'
    && line.substituteStatus !== 'pending_manager';
}

interface Props {
  requestId: string;
  subTab: RequestsSubTab;
  initialRequest?: StockRequest;
  onRequestUpdated?: () => void;
}

interface ApprovalDraft {
  approvedQty: number;
  approvalReason: string;
  matchItemId: string;
  resolutionNotes: string;
  substituteItemId: string;
  unavailable: boolean;
  unavailableReason: string;
  lineDisposition: 'approve' | 'unavailable' | 'substitute' | null;
  dispositionConfirmed: boolean;
  poReference: string;
  supplier: string;
  expectedDate: string;
  skipOrder?: boolean;
}

function isLineActionComplete(line: StockRequestLine, d: ApprovalDraft | undefined): boolean {
  if (!d?.dispositionConfirmed) return false;
  if (d.lineDisposition === 'unavailable') return !!d.unavailableReason?.trim();
  if (d.lineDisposition === 'substitute') return !!d.substituteItemId?.trim();
  if (lineNeedsResolution(line) && !d.matchItemId?.trim()) return false;
  const approvedQty = d.approvedQty ?? line.requestedQty;
  if (approvedQty < line.requestedQty && !d.approvalReason?.trim()) return false;
  return true;
}

function approvalDraftFromLine(line: StockRequestLine): ApprovalDraft {
  const draft: ApprovalDraft = {
    approvedQty: line.approvedQty ?? line.requestedQty,
    approvalReason: line.approvalReason ?? '',
    matchItemId: line.itemId ?? '',
    resolutionNotes: line.resolutionNotes ?? '',
    substituteItemId: line.substituteItemId ?? '',
    unavailable: line.status === 'unavailable',
    unavailableReason: line.status === 'unavailable' ? (line.approvalReason ?? '') : '',
    lineDisposition: null,
    dispositionConfirmed: false,
    poReference: line.poReference ?? '',
    supplier: line.supplier ?? '',
    expectedDate: line.expectedDate ?? '',
    // Ordering checkboxes start unchecked — user must opt in each line.
    skipOrder: true,
  };

  if (line.substituteStatus) return draft;

  if (line.status === 'unavailable') {
    draft.lineDisposition = 'unavailable';
    draft.dispositionConfirmed = true;
    draft.unavailable = true;
    return draft;
  }

  if (line.status === 'submitted' || line.status === 'pending_resolution') {
    if (line.substituteItemId) {
      draft.lineDisposition = 'substitute';
      draft.dispositionConfirmed = true;
      draft.substituteItemId = line.substituteItemId;
      if (line.approvedQty != null) draft.approvedQty = line.approvedQty;
      if (line.approvalReason) draft.approvalReason = line.approvalReason;
    } else if (line.approvedQty != null) {
      draft.lineDisposition = 'approve';
      draft.dispositionConfirmed = true;
      draft.approvedQty = line.approvedQty;
      draft.approvalReason = line.approvalReason ?? '';
    } else if (line.approvalReason?.trim()) {
      draft.lineDisposition = 'unavailable';
      draft.dispositionConfirmed = true;
      draft.unavailableReason = line.approvalReason;
      draft.unavailable = true;
      draft.approvedQty = 0;
    }
  }

  return draft;
}

const RequestExpandedPanel: React.FC<Props> = ({ requestId, subTab, initialRequest, onRequestUpdated }) => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';
  const theme = getRequestTheme(isDark);
  const { items } = useInventoryStore();
  const { user } = useAuthStore();
  const fetchSuppliers = useSuppliersStore(s => s.fetchSuppliers);
  const {
    loadRequestDetail,
    fetchRequests,
    saveLineReviewDraft,
    approveRequest,
    sendForRequesterReview,
    markOrdered,
    receiveLines,
    cancelRequest,
    acceptSubstitute,
    declineSubstitute,
    counterPropose,
    cancelLine,
    backorderLine,
    splitLine,
    disputeLine,
    resolveDispute,
  } = useStockRequestStore();

  const [req, setReq] = useState<StockRequest | null>(() => initialRequest?.lines ? initialRequest : null);
  const [detailLoading, setDetailLoading] = useState(!initialRequest?.lines);
  const [approvals, setApprovals] = useState<Record<string, ApprovalDraft>>({});
  const [saving, setSaving] = useState(false);
  const [showReceiveForm, setShowReceiveForm] = useState(false);
  const [receiveDrafts, setReceiveDrafts] = useState<Record<string, LineReceiveDraft>>({});
  const [skipSerialReceive, setSkipSerialReceive] = useState(false);
  const [skipSerialReason, setSkipSerialReason] = useState('');
  const [orderFieldHover, setOrderFieldHover] = useState<'po' | 'supplier' | 'eta' | 'ordered' | null>(null);
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);
  const showMessage = (text: string, isError = false) => {
    setMessage(text);
    setMessageIsError(isError);
  };
  const clearMessage = () => {
    setMessage('');
    setMessageIsError(false);
  };
  // Per-line action panels (cancel / backorder) after approval
  type LineAction = { type: 'cancel' | 'backorder'; reason: string; expectedDate: string };
  const [lineActions, setLineActions] = useState<Record<string, LineAction>>({});
  const patchLineAction = (lineId: string, patch: Partial<LineAction>) =>
    setLineActions(prev => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        ...patch,
        type: patch.type ?? prev[lineId]?.type ?? 'cancel',
        reason: patch.reason ?? prev[lineId]?.reason ?? '',
        expectedDate: patch.expectedDate ?? prev[lineId]?.expectedDate ?? '',
      },
    }));

  // Quick-create new catalogue item from free-text line
  type QuickCreate = { name: string; category: string; requiresSerial: boolean; requiresMac: boolean; saving: boolean };
  const [quickCreate, setQuickCreate] = useState<Record<string, QuickCreate>>({});
  const patchQC = (lineId: string, patch: Partial<QuickCreate>) =>
    setQuickCreate(prev => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        ...patch,
        name: patch.name ?? prev[lineId]?.name ?? '',
        category: patch.category ?? prev[lineId]?.category ?? '',
        requiresSerial: patch.requiresSerial ?? prev[lineId]?.requiresSerial ?? false,
        requiresMac: patch.requiresMac ?? prev[lineId]?.requiresMac ?? false,
        saving: patch.saving ?? prev[lineId]?.saving ?? false,
      },
    }));

  // Split line state
  type SplitDraft = { qty: number; itemId: string; saving: boolean };
  const [splitDrafts, setSplitDrafts] = useState<Record<string, SplitDraft>>({});
  const patchSplit = (lineId: string, patch: Partial<SplitDraft>) =>
    setSplitDrafts(prev => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        ...patch,
        qty: patch.qty ?? prev[lineId]?.qty ?? 1,
        itemId: patch.itemId ?? prev[lineId]?.itemId ?? '',
        saving: patch.saving ?? prev[lineId]?.saving ?? false,
      },
    }));

  const [substituteErrors, setSubstituteErrors] = useState<Set<string>>(() => new Set());
  const [supplierErrors, setSupplierErrors] = useState<Set<string>>(() => new Set());
  const [showActivity, setShowActivity] = useState(false);

  const refreshDetail = useCallback(async () => {
    const detail = await loadRequestDetail(requestId);
    setReq(detail);
    await fetchRequests();
    onRequestUpdated?.();
  }, [requestId, loadRequestDetail, fetchRequests, onRequestUpdated]);

  const persistLineReview = useCallback(async (
    line: StockRequestLine,
    draft: ApprovalDraft,
    options?: { clear?: boolean; matchOnly?: boolean },
  ) => {
    if (!req) return false;
    const input: Parameters<typeof saveLineReviewDraft>[1] = { lineId: line.id };

    if (options?.clear) {
      input.disposition = null;
    } else if (!options?.matchOnly && draft.dispositionConfirmed && draft.lineDisposition) {
      input.disposition = draft.lineDisposition;
      input.approvedQty = draft.approvedQty;
      input.approvalReason = draft.approvalReason?.trim() || null;
      input.unavailableReason = draft.unavailableReason?.trim() || null;
      input.substituteItemId = draft.substituteItemId?.trim() || null;
    }

    if (draft.matchItemId?.trim()) input.matchItemId = draft.matchItemId.trim();
    if (draft.resolutionNotes?.trim()) input.resolutionNotes = draft.resolutionNotes.trim();

    const ok = await saveLineReviewDraft(req.id, input);
    if (ok) {
      const detail = await loadRequestDetail(requestId);
      setReq(detail);
      await fetchRequests();
      onRequestUpdated?.();
    } else {
      showMessage(useStockRequestStore.getState().error ?? 'Failed to save line review.', true);
    }
    return ok;
  }, [req, saveLineReviewDraft, loadRequestDetail, requestId, fetchRequests, onRequestUpdated]);

  useEffect(() => {
    let cancelled = false;
    void useInventoryStore.getState().fetchItems();

    const store = useStockRequestStore.getState();
    const seed = store.requests.find(r => r.id === requestId);

    if (seed?.lines) {
      setReq(seed);
      setDetailLoading(false);
      return () => { cancelled = true; };
    }

    setDetailLoading(true);
    void store.loadRequestDetail(requestId).then(detail => {
      if (cancelled) return;
      setReq(detail);
      setDetailLoading(false);
    });
    return () => { cancelled = true; };
  }, [requestId]);

  const linesSignature = (req?.lines ?? [])
    .map(l => `${l.id}:${l.status}:${l.approvedQty ?? ''}:${l.substituteItemId ?? ''}:${l.substituteStatus ?? ''}:${l.approvalReason ?? ''}:${l.itemId ?? ''}:${l.poReference ?? ''}:${l.supplier ?? ''}:${l.expectedDate ?? ''}:${l.receivedQty}`)
    .join('|');

  const approvalsRequestIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!req?.lines) return;
    const requestChanged = approvalsRequestIdRef.current !== req.id;
    approvalsRequestIdRef.current = req.id;

    setApprovals(prev => {
      const draft: Record<string, ApprovalDraft> = {};
      for (const line of req.lines!) {
        const fromLine = approvalDraftFromLine(line);
        const existing = !requestChanged ? prev[line.id] : undefined;
        // Keep in-progress PO / supplier / ETA edits when another line is marked ordered
        // and the request reloads — only replace drafts for lines already persisted as ordered.
        if (existing && line.status === 'approved') {
          draft[line.id] = {
            ...fromLine,
            poReference: existing.poReference ?? fromLine.poReference,
            supplier: existing.supplier ?? fromLine.supplier,
            expectedDate: existing.expectedDate ?? fromLine.expectedDate,
            skipOrder: existing.skipOrder,
          };
        } else {
          draft[line.id] = fromLine;
        }
      }
      return draft;
    });
    setSubstituteErrors(new Set());
    setSupplierErrors(new Set());
    setShowActivity(false);
  }, [req?.id, linesSignature]);

  const isAssignedToMe = !req?.assignedTo || req.assignedTo === user?.id;
  const lines = req?.lines ?? [];

  const canReview = subTab === 'to_review' && isAssignedToMe
    && isReviewAssignment(req?.assignmentStep, req?.status ?? '')
    && (req?.status === 'submitted' || req?.status === 'approved');
  const hasReceivedStock = lines.some(l => l.receivedQty > 0);
  const showReceived = subTab === 'receiving' && hasReceivedStock;
  const canReceive = subTab === 'receiving'
    && !!req
    && ['partially_ordered', 'ordered', 'partially_received'].includes(req.status);
  const canMarkOrdered = subTab === 'to_review' && isAssignedToMe
    && (req?.status === 'approved' || req?.status === 'partially_ordered')
    && isOrderAssignment(req?.assignmentStep, req?.status ?? '', lines);
  const isOrderedView = !!req && ORDERED_REQUEST_STATUSES.has(req.status);
  const isEditingOrder = canMarkOrdered;
  const isEditingReceive = showReceiveForm;
  const showOrderColumns = (isEditingOrder || isOrderedView) && !isEditingReceive;
  const showReceivingEdit = isEditingReceive;

  useEffect(() => {
    if (isEditingOrder) void fetchSuppliers();
  }, [isEditingOrder, fetchSuppliers]);

  const lineColumns = useMemo(() => {
    if (isEditingReceive) {
      return RECEIVING_EDIT_LINE_COLUMNS.filter(col => showReceived || col.key !== 'received');
    }
    if (isEditingOrder) return ORDERING_LINE_COLUMNS;
    if (isOrderedView) {
      const cols: { key: string; width: number | string }[] = [
        ORDERING_VIEW_COLUMN_DEFS.item,
        ORDERING_VIEW_COLUMN_DEFS.qty,
        ORDERING_VIEW_COLUMN_DEFS.po,
        ORDERING_VIEW_COLUMN_DEFS.supplier,
        ORDERING_VIEW_COLUMN_DEFS.eta,
      ];
      if (showReceived) cols.push(NORMAL_LINE_COLUMN_DEFS.received);
      return cols;
    }
    const cols: { key: string; width: number | string }[] = [
      NORMAL_LINE_COLUMN_DEFS.item,
      NORMAL_LINE_COLUMN_DEFS.status,
      NORMAL_LINE_COLUMN_DEFS.requested,
      NORMAL_LINE_COLUMN_DEFS.approved,
    ];
    if (showReceived) cols.push(NORMAL_LINE_COLUMN_DEFS.received);
    if (canReview) cols.push(NORMAL_LINE_COLUMN_DEFS.actions);
    return cols;
  }, [isEditingOrder, isOrderedView, showReceived, canReview, isEditingReceive]);
  const lineColSpan = lineColumns.length;
  const canRequesterReview = req?.status === 'submitted'
    && req?.assignmentStep === 'requester'
    && req?.requesterId === user?.id;
  const canRespondAsManager = !!user?.id
    && (req?.reviewedBy === user.id || req?.assignedTo === user.id);
  const hasManagerCounters = hasPendingManagerReview(lines);

  const lineLabel = (line: StockRequestLine) => {
    if (line.lineType === 'free_text' && !line.itemId && !line.substituteItemId) return line.freeTextDescription ?? '—';
    const id = effectiveLineItemId(line);
    return items.find(i => i.id === id)?.name
      ?? items.find(i => i.id === line.itemId)?.name
      ?? line.freeTextDescription
      ?? '—';
  };

  const originalRequestedItemName = (line: StockRequestLine) =>
    items.find(i => i.id === line.itemId)?.name ?? line.freeTextDescription ?? '—';

  const renderLineItemName = (line: StockRequestLine) => {
    if (line.substituteItemId && line.substituteItemId !== line.itemId) {
      const substituteName = items.find(i => i.id === line.substituteItemId)?.name ?? '—';
      const original = originalRequestedItemName(line);
      const title = `${original} > ${substituteName}`;
      return (
        <span className="request-line-item-label flex-1 min-w-0 font-normal" title={title} style={{ fontWeight: 400 }}>
          <span style={{ color: isDark ? '#f87171' : '#dc2626', fontWeight: 400 }}>{original}</span>
          {' > '}
          <span style={{ color: isDark ? '#4ade80' : '#16a34a', fontWeight: 400 }}>{substituteName}</span>
        </span>
      );
    }
    const label = lineLabel(line);
    return <span className="request-line-item-label flex-1 min-w-0" title={label}>{label}</span>;
  };

  const formatActivityDescription = (ev: StockRequestEvent) => {
    const payload = ev.payload as { substituteName?: string; originalName?: string };
    if (payload.substituteName && payload.originalName) {
      return ev.description ?? `${payload.substituteName} substituted for ${payload.originalName}`;
    }
    if (ev.description?.includes('substituted for')) return ev.description;

    const line = ev.lineId ? (req?.lines ?? []).find(l => l.id === ev.lineId) : undefined;
    if (line?.substituteItemId && line.substituteItemId !== line.itemId) {
      const substituteName = items.find(i => i.id === line.substituteItemId)?.name ?? 'Unknown item';
      const originalName = originalRequestedItemName(line);
      const base = ev.description ?? ev.eventType;
      if (base.includes(originalName) || base.includes(substituteName)) return base;
      return `${base}: ${substituteName} substituted for ${originalName}`;
    }
    return ev.description ?? ev.eventType;
  };

  const approvedLines = useMemo(
    () => (req?.lines ?? []).filter(l => l.status === 'approved'),
    [req?.lines],
  );

  const toggleSelectAllOrders = (selected: boolean) => {
    const patch: Record<string, ApprovalDraft> = {};
    approvedLines.forEach(l => {
      patch[l.id] = {
        ...approvals[l.id],
        approvedQty: approvals[l.id]?.approvedQty ?? l.requestedQty,
        approvalReason: approvals[l.id]?.approvalReason ?? '',
        skipOrder: !selected,
      };
    });
    setApprovals(prev => ({ ...prev, ...patch }));
  };

  const allOrderLinesSelected =
    approvedLines.length > 0 && approvedLines.every(l => !approvals[l.id]?.skipOrder);
  const selectedOrderLineIds = useMemo(
    () => approvedLines.filter(l => !approvals[l.id]?.skipOrder).map(l => l.id),
    [approvedLines, approvals],
  );
  const multiOrderSelected = selectedOrderLineIds.length > 1;
  const orderCellHoverBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)';

  useEffect(() => {
    if (!multiOrderSelected || !isEditingOrder) setOrderFieldHover(null);
  }, [multiOrderSelected, isEditingOrder]);

  const patchOrderField = (lineId: string, patch: Partial<ApprovalDraft>) => {
    setApprovals(prev => {
      const lineSelected = !prev[lineId]?.skipOrder;
      const selectedIds = approvedLines
        .filter(l => !prev[l.id]?.skipOrder)
        .map(l => l.id);
      const targets = lineSelected && selectedIds.length > 1 ? selectedIds : [lineId];
      const next = { ...prev };
      for (const id of targets) {
        const existing = next[id];
        const fallbackLine = approvedLines.find(l => l.id === id);
        next[id] = {
          approvedQty: existing?.approvedQty ?? fallbackLine?.requestedQty ?? 1,
          approvalReason: existing?.approvalReason ?? '',
          matchItemId: existing?.matchItemId ?? '',
          resolutionNotes: existing?.resolutionNotes ?? '',
          substituteItemId: existing?.substituteItemId ?? '',
          unavailable: existing?.unavailable ?? false,
          unavailableReason: existing?.unavailableReason ?? '',
          lineDisposition: existing?.lineDisposition ?? null,
          dispositionConfirmed: existing?.dispositionConfirmed ?? false,
          poReference: existing?.poReference ?? '',
          supplier: existing?.supplier ?? '',
          expectedDate: existing?.expectedDate ?? '',
          skipOrder: existing?.skipOrder,
          ...patch,
        };
      }
      return next;
    });
    if (patch.supplier !== undefined) {
      setSupplierErrors(prev => {
        if (prev.size === 0) return prev;
        const lineSelected = !approvals[lineId]?.skipOrder;
        const selectedIds = approvedLines
          .filter(l => !approvals[l.id]?.skipOrder)
          .map(l => l.id);
        const targets = lineSelected && selectedIds.length > 1 ? selectedIds : [lineId];
        const next = new Set(prev);
        for (const id of targets) {
          if (patch.supplier?.trim()) next.delete(id);
        }
        return next;
      });
    }
  };

  const handleOrderFieldHover = (field: 'po' | 'supplier' | 'eta' | 'ordered', lineSelected: boolean, hovered: boolean) => {
    if (hovered && lineSelected && multiOrderSelected) {
      setOrderFieldHover(field);
    } else if (!hovered && orderFieldHover === field) {
      setOrderFieldHover(null);
    }
  };

  const isLineOrderable = (line: StockRequestLine) => line.status === 'approved';

  const receivableLines = useMemo(
    () => (req?.lines ?? []).filter(isLineReceivable),
    [req?.lines],
  );

  const receivableLineIds = useMemo(
    () => receivableLines.map(l => l.id),
    [receivableLines],
  );

  const startReceiveForm = () => {
    const drafts: Record<string, LineReceiveDraft> = {};
    for (const line of receivableLines) {
      const remaining = remainingReceiveQty(line);
      drafts[line.id] = {
        qty: remaining,
        units: Array.from({ length: remaining }, () => ({ serial: '', mac: '' })),
        dispute: false,
        disputeReason: '',
        include: true,
      };
    }
    setReceiveDrafts(drafts);
    setSkipSerialReceive(false);
    setSkipSerialReason('');
    setShowReceiveForm(true);
  };

  const patchReceiveDraft = (lineId: string, patch: Partial<LineReceiveDraft>) =>
    setReceiveDrafts(prev => {
      const current = prev[lineId];
      if (!current) return prev;
      return { ...prev, [lineId]: { ...current, ...patch } };
    });

  const updateReceiveQty = (lineId: string, qty: number, line: StockRequestLine) => {
    const max = remainingReceiveQty(line);
    const n = Math.min(Math.max(1, qty), max);
    setReceiveDrafts(prev => {
      const d = prev[lineId];
      if (!d) return prev;
      return {
        ...prev,
        [lineId]: {
          ...d,
          qty: n,
          units: Array.from({ length: n }, (_, i) => d.units[i] ?? { serial: '', mac: '' }),
        },
      };
    });
  };

  const updateReceiveUnit = (lineId: string, idx: number, field: 'serial' | 'mac', value: string) => {
    setReceiveDrafts(prev => {
      const d = prev[lineId];
      if (!d) return prev;
      const units = [...d.units];
      units[idx] = { ...units[idx], [field]: value };
      return { ...prev, [lineId]: { ...d, units } };
    });
  };

  const allReceiveLinesSelected = receivableLineIds.length > 0
    && receivableLineIds.every(id => receiveDrafts[id]?.include !== false);

  const toggleSelectAllReceive = (selected: boolean) => {
    setReceiveDrafts(prev => {
      const next = { ...prev };
      for (const id of receivableLineIds) {
        if (next[id]) next[id] = { ...next[id], include: selected };
      }
      return next;
    });
  };

  const noReceiveLinesSelected = receivableLineIds.every(id => {
    const rd = receiveDrafts[id];
    return !rd?.include || rd.dispute;
  });

  const serialReceiveProgress = useMemo(() => {
    let required = 0;
    let filled = 0;
    if (!showReceiveForm) return { required, filled };
    for (const line of receivableLines) {
      const rd = receiveDrafts[line.id];
      if (!rd?.include || rd.dispute) continue;
      const itemId = effectiveLineItemId(line);
      const item = items.find(i => i.id === itemId);
      if (!item) continue;
      for (let i = 0; i < rd.qty; i++) {
        if (item.requiresSerial) {
          required++;
          if (rd.units[i]?.serial?.trim()) filled++;
        }
        if (item.requiresMac) {
          required++;
          if (rd.units[i]?.mac?.trim()) filled++;
        }
      }
    }
    return { required, filled };
  }, [receivableLines, items, receiveDrafts, showReceiveForm]);

  const canConfirmReceive = skipSerialReceive
    ? skipSerialReason.trim().length > 0
    : serialReceiveProgress.required === 0 || serialReceiveProgress.filled >= serialReceiveProgress.required;

  const orderReadCell = (value: string | null | undefined) => (
    <span
      className="inline-flex items-center h-5 max-h-5 text-sm min-w-0 max-w-full truncate"
      style={{ color: value ? theme.text : theme.muted }}
      title={value ?? undefined}
    >
      {value || ''}
    </span>
  );

  const supplierBadgeCell = (value: string | null | undefined) => {
    if (!value) return orderReadCell(null);
    return (
      <span
        className={`${supplierBadgeClass} align-middle`}
        style={supplierBadgeStyle(value, isDark)}
        title={value}
      >
        {value}
      </span>
    );
  };

  const draftNeedsRequesterReview = useMemo(() => {
    if (!req?.lines || !canReview) return false;
    return req.lines.some(line => {
      if (line.status === 'cancelled') return false;
      if (line.substituteStatus === 'accepted') return false;
      if (line.substituteStatus === 'pending_manager') return false;
      const d = approvals[line.id];
      if (d?.lineDisposition === 'unavailable' && d.dispositionConfirmed) return true;
      if (d?.lineDisposition === 'substitute' && d.dispositionConfirmed && d.substituteItemId) return true;
      const approvedQty = d?.approvedQty ?? line.requestedQty;
      if (approvedQty < line.requestedQty) return true;
      return false;
    });
  }, [req?.lines, approvals, canReview]);

  const reviewableLines = useMemo(() => {
    if (!req?.lines || !canReview || req.status !== 'submitted') return [];
    return req.lines.filter(isEditableReviewLine);
  }, [req?.lines, canReview, req?.status]);

  // Note: intentionally NOT gated on reviewableLines.length > 0 — once every
  // line has already been resolved (e.g. via a prior accepted substitute
  // negotiation, or cancelled), there's nothing left to review and the
  // request must still be approvable.
  const allReviewLinesActioned = useMemo(
    () => reviewableLines.every(line => isLineActionComplete(line, approvals[line.id])),
    [reviewableLines, approvals],
  );

  const inputCls = 'text-xs px-2 py-1.5 rounded border w-full focus:outline-none focus:ring-1 focus:ring-blue-500';
  const activityLabelColor = isDark ? '#6b7280' : '#4b5563';
  const qtyInputCls = 'request-line-qty-input movement-qty-input text-center text-sm w-14 h-5 px-1 py-0 rounded border box-border leading-none focus:outline-none';
  const qtyHighlightCls = 'request-line-qty-input--highlight';
  const qtyOutlineCls = 'request-line-qty-outline inline-flex items-center justify-center w-14 h-5 text-sm px-1 rounded border box-border leading-none tabular-nums';

  const dispoIconBtnCls = 'h-5 w-5 inline-flex items-center justify-center rounded transition-colors flex-shrink-0';
  const dispoColor: Record<'approve' | 'unavailable' | 'substitute', string> = {
    approve: isDark ? '#4ade80' : '#16a34a',
    unavailable: isDark ? '#f87171' : '#dc2626',
    substitute: isDark ? '#60a5fa' : '#2563eb',
  };
  const dispoActiveBg: Record<'approve' | 'unavailable' | 'substitute', string> = {
    approve: isDark ? 'rgba(34,197,94,0.16)' : 'rgba(34,197,94,0.12)',
    unavailable: isDark ? 'rgba(239,68,68,0.16)' : 'rgba(239,68,68,0.12)',
    substitute: isDark ? 'rgba(59,130,246,0.16)' : 'rgba(59,130,246,0.12)',
  };
  const dispoPendingBg: Record<'approve' | 'unavailable' | 'substitute', string> = {
    approve: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
    unavailable: dispoActiveBg.unavailable,
    substitute: dispoActiveBg.substitute,
  };
  const dispoRowBg: Record<'approve' | 'unavailable' | 'substitute', string> = {
    approve: isDark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)',
    unavailable: isDark ? 'rgba(239,68,68,0.1)' : 'rgba(254,226,226,0.35)',
    substitute: isDark ? 'rgba(59,130,246,0.1)' : 'rgba(219,234,254,0.45)',
  };
  const detailBtnPrimaryCls = 'text-[11px] px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 flex-shrink-0';
  const detailBtnSecondaryCls = 'text-[11px] px-2 py-1 rounded transition-colors flex-shrink-0';

  const footerActionBtnCls = 'inline-flex items-center justify-center gap-1.5 h-7 px-2.5 rounded text-xs font-medium transition-colors flex-shrink-0 whitespace-nowrap border disabled:opacity-50';
  const footerSecondaryBtnStyle: React.CSSProperties = {
    borderColor: theme.border,
    color: theme.text,
    backgroundColor: isDark ? '#1c2129' : '#f3f4f6',
  };
  const qtyInputStyle: React.CSSProperties = {
    backgroundColor: theme.inputBg,
    borderColor: theme.border,
    color: theme.text,
    colorScheme: isDark ? 'dark' : 'light',
  };
  const qtyFieldStyle = (highlight: boolean): React.CSSProperties => ({
    ...qtyInputStyle,
    borderColor: highlight ? 'rgba(245, 158, 11, 0.7)' : theme.border,
  });
  const dispositionFieldStyle: React.CSSProperties = {
    backgroundColor: theme.cardBg,
    borderColor: theme.border,
    color: theme.text,
    colorScheme: isDark ? 'dark' : 'light',
  };

  const patchApproval = (lineId: string, patch: Partial<ApprovalDraft>) => {
    setApprovals(prev => {
      const current = prev[lineId];
      const next = { ...current, ...patch };
      if (
        patch.approvedQty !== undefined
        && current?.lineDisposition === 'approve'
        && patch.dispositionConfirmed === undefined
      ) {
        next.dispositionConfirmed = false;
      }
      return { ...prev, [lineId]: next };
    });
  };

  const resetLineDisposition = (lineId: string, line: StockRequestLine) => {
    setSubstituteErrors(prev => {
      if (!prev.has(lineId)) return prev;
      const next = new Set(prev);
      next.delete(lineId);
      return next;
    });
    patchApproval(lineId, {
      lineDisposition: null,
      dispositionConfirmed: false,
      unavailable: false,
      substituteItemId: '',
      unavailableReason: '',
      approvedQty: line.requestedQty,
    });
    clearMessage();
    void persistLineReview(line, {
      approvedQty: line.requestedQty,
      approvalReason: '',
      matchItemId: line.itemId ?? '',
      resolutionNotes: line.resolutionNotes ?? '',
      substituteItemId: '',
      unavailable: false,
      unavailableReason: '',
      lineDisposition: null,
      dispositionConfirmed: false,
      poReference: line.poReference ?? '',
      supplier: line.supplier ?? '',
      expectedDate: line.expectedDate ?? '',
    }, { clear: true });
  };

  const applyLineDisposition = (
    lineId: string,
    line: StockRequestLine,
    disposition: ApprovalDraft['lineDisposition'],
    confirmed: boolean,
  ) => {
    const d = approvals[lineId];
    if (disposition !== 'substitute') {
      setSubstituteErrors(prev => {
        if (!prev.has(lineId)) return prev;
        const next = new Set(prev);
        next.delete(lineId);
        return next;
      });
    }
    patchApproval(lineId, {
      lineDisposition: disposition,
      dispositionConfirmed: confirmed,
      unavailable: disposition === 'unavailable' && confirmed,
      approvedQty: disposition === 'unavailable' && confirmed
        ? 0
        : ((d?.approvedQty ?? 0) > 0 && !(disposition === 'unavailable' && confirmed))
          ? d!.approvedQty
          : line.requestedQty,
      substituteItemId: disposition === 'substitute' ? (d?.substituteItemId ?? '') : '',
      unavailableReason: disposition === 'unavailable' ? (d?.unavailableReason ?? '') : '',
      approvalReason: disposition === 'approve' ? (d?.approvalReason ?? '') : '',
    });
  };

  const toggleLineDisposition = (
    lineId: string,
    line: StockRequestLine,
    disposition: ApprovalDraft['lineDisposition'],
  ) => {
    const d = approvals[lineId];
    const current = d?.lineDisposition ?? null;

    if (current === disposition) {
      if (disposition === 'approve' && !d?.dispositionConfirmed) {
        confirmApprovedQty(lineId, line);
        return;
      }
      resetLineDisposition(lineId, line);
      return;
    }

    applyLineDisposition(lineId, line, disposition, false);
  };

  const confirmApprovedQty = (lineId: string, line: StockRequestLine) => {
    const d = approvals[lineId];
    if (!d || d.lineDisposition !== 'approve') return;
    const approvedQty = d.approvedQty ?? line.requestedQty;
    if (approvedQty <= 0) {
      showMessage('Approved qty must be at least 1.', true);
      return;
    }
    if (lineNeedsResolution(line) && !d.matchItemId?.trim()) {
      showMessage('Match this line to a catalogue item before approving.', true);
      return;
    }
    if (approvedQty < line.requestedQty && !d.approvalReason?.trim()) {
      showMessage('Provide a reason for reduced qty before confirming.', true);
      return;
    }
    applyLineDisposition(lineId, line, 'approve', true);
    clearMessage();
    void persistLineReview(line, {
      ...d,
      lineDisposition: 'approve',
      dispositionConfirmed: true,
      approvedQty,
      approvalReason: approvedQty < line.requestedQty ? d.approvalReason : '',
    });
  };

  const confirmLineDisposition = (lineId: string, line: StockRequestLine) => {
    const d = approvals[lineId];
    if (!d) return;
    if (d.lineDisposition === 'unavailable') {
      if (!d.unavailableReason?.trim()) {
        showMessage('Provide a reason before confirming unavailable.', true);
        return;
      }
      applyLineDisposition(lineId, line, 'unavailable', true);
      clearMessage();
      void persistLineReview(line, {
        ...d,
        lineDisposition: 'unavailable',
        dispositionConfirmed: true,
        unavailable: true,
        approvedQty: 0,
      });
      return;
    }
    if (d.lineDisposition === 'substitute') {
      if (!d.substituteItemId?.trim()) {
        setSubstituteErrors(prev => new Set(prev).add(lineId));
        return;
      }
      setSubstituteErrors(prev => {
        if (!prev.has(lineId)) return prev;
        const next = new Set(prev);
        next.delete(lineId);
        return next;
      });
      applyLineDisposition(lineId, line, 'substitute', true);
      clearMessage();
      void persistLineReview(line, {
        ...d,
        lineDisposition: 'substitute',
        dispositionConfirmed: true,
      });
    }
  };

  const handleApprove = async () => {
    if (!req?.lines) return;
    setMessage('');
    setMessageIsError(false);

    const lineApprovals: LineApprovalInput[] = req.lines
      .filter(l => l.status !== 'cancelled' && l.status !== 'unavailable')
      .map(line => {
        const d = approvals[line.id];
        const isUnavailable = d?.lineDisposition === 'unavailable' && d.dispositionConfirmed;
        if (isUnavailable) {
          return {
            lineId: line.id,
            approvedQty: 0,
            status: 'unavailable' as const,
            approvalReason: d.unavailableReason?.trim() || 'Unavailable',
            itemId: d.matchItemId || undefined,
            resolutionNotes: d.resolutionNotes?.trim() || null,
          };
        }

        const approvedQty = d?.approvedQty ?? line.requestedQty;
        const input: LineApprovalInput = {
          lineId: line.id,
          approvedQty,
          approvalReason: approvedQty < line.requestedQty ? d?.approvalReason?.trim() || null : null,
        };
        if (lineNeedsResolution(line) && d?.matchItemId) {
          input.itemId = d.matchItemId;
          input.resolutionNotes = d.resolutionNotes?.trim() || null;
        }
        if (d?.lineDisposition === 'substitute' && d.dispositionConfirmed && d.substituteItemId) {
          input.substituteItemId = d.substituteItemId;
        }
        return input;
      });

    const missingSubstitute: string[] = [];
    for (const la of lineApprovals) {
      const line = req.lines!.find(l => l.id === la.lineId)!;
      const d = approvals[line.id];
      if (d?.lineDisposition === 'unavailable' && d.dispositionConfirmed) {
        if (!d.unavailableReason?.trim()) {
          showMessage('Provide a reason for unavailable lines.', true);
          return;
        }
        continue;
      }
      if (d?.lineDisposition === 'substitute' && d.dispositionConfirmed && !d.substituteItemId?.trim()) {
        missingSubstitute.push(line.id);
        continue;
      }
      if (la.approvedQty < line.requestedQty && !la.approvalReason?.trim()) {
        showMessage('Please provide a reason when approving less than requested.', true);
        return;
      }
      if (lineNeedsResolution(line) && !la.itemId) {
        showMessage('Match all free-text lines to catalogue items before approving.', true);
        return;
      }
    }

    if (missingSubstitute.length > 0) {
      setSubstituteErrors(new Set(missingSubstitute));
      showMessage('Select a substitute item for each line marked as propose substitute.', true);
      return;
    }
    setSubstituteErrors(new Set());

    setSaving(true);

    const ok = draftNeedsRequesterReview
      ? await sendForRequesterReview(req.id, lineApprovals)
      : await approveRequest(req.id, lineApprovals);
    setSaving(false);
    if (ok) await refreshDetail();
    if (!ok) {
      showMessage('Action failed.', true);
    } else {
      clearMessage();
    }
  };

  const handleConfirmOrdered = async (lineId: string) => {
    if (!req?.lines || saving) return;

    const lineSelected = !approvals[lineId]?.skipOrder;
    const selectedIds = approvedLines
      .filter(l => !approvals[l.id]?.skipOrder)
      .map(l => l.id);
    const targetIds = lineSelected && selectedIds.length > 1 ? selectedIds : [lineId];
    const toOrderLines = (req.lines ?? []).filter(
      l => l.status === 'approved' && targetIds.includes(l.id),
    );
    if (toOrderLines.length === 0) {
      showMessage('Select at least one line to order.', true);
      return;
    }

    const missingSupplier = toOrderLines.filter(line => {
      const supplier = approvals[line.id]?.supplier?.trim() || line.supplier?.trim();
      return !supplier;
    });
    if (missingSupplier.length > 0) {
      setSupplierErrors(new Set(missingSupplier.map(l => l.id)));
      showMessage(
        missingSupplier.length === 1
          ? 'Select a supplier before marking the line as ordered.'
          : 'Select a supplier for each line before marking as ordered.',
        true,
      );
      return;
    }
    setSupplierErrors(new Set());

    setSaving(true);
    const lineOrders: LineOrderInput[] = toOrderLines.map(line => {
      const d = approvals[line.id];
      return {
        lineId: line.id,
        poReference: d?.poReference?.trim() || null,
        supplier: d?.supplier?.trim() || null,
        expectedDate: d?.expectedDate || null,
      };
    });

    const ok = await markOrdered(req.id, lineOrders);
    setSaving(false);
    if (!ok) {
      showMessage(useStockRequestStore.getState().error ?? 'Failed to mark as ordered.', true);
      return;
    }

    const orderByLine = new Map(lineOrders.map(o => [o.lineId, o]));
    setReq(prev => {
      if (!prev) return prev;
      const nextLines = (prev.lines ?? []).map(l => {
        const order = orderByLine.get(l.id);
        if (!order) return l;
        return {
          ...l,
          status: 'ordered' as const,
          poReference: order.poReference ?? l.poReference,
          supplier: order.supplier ?? l.supplier,
          expectedDate: order.expectedDate ?? l.expectedDate,
        };
      });
      return {
        ...prev,
        status: deriveRequestStatus(nextLines),
        lines: nextLines,
      };
    });

    setOrderFieldHover(null);
    await refreshDetail();
  };

  const handleCancel = async () => {
    if (!req || !window.confirm('Cancel this request?')) return;
    await cancelRequest(req.id);
    await refreshDetail();
  };

  const handleConfirmReceive = async () => {
    if (!req) return;
    setSaving(true);
    clearMessage();

    const disputed = receivableLines.filter(l => receiveDrafts[l.id]?.dispute);
    for (const line of disputed) {
      await disputeLine(req.id, line.id, receiveDrafts[line.id].disputeReason || 'Wrong item / mismatch');
    }

    const receiveable = receivableLines.filter(l => {
      const rd = receiveDrafts[l.id];
      return rd?.include !== false && !rd?.dispute && rd.qty > 0;
    });

    if (receiveable.length > 0) {
      const receipts: LineReceiveInput[] = receiveable.map(line => {
        const rd = receiveDrafts[line.id];
        return {
          lineId: line.id,
          qty: rd.qty,
          units: rd.units.map(u => ({ serial: u.serial, mac: u.mac })),
          skipSerialValidation: skipSerialReceive,
          skipReason: skipSerialReceive ? skipSerialReason.trim() : undefined,
        };
      });

      const result = await receiveLines(req.id, receipts);
      if (!result.success) {
        setSaving(false);
        showMessage(result.message, true);
        return;
      }
    }

    setSaving(false);
    setShowReceiveForm(false);
    if (receiveable.length > 0 || disputed.length > 0) {
      showMessage('Stock received and added to inventory.');
    }
    await refreshDetail();
  };

  if (detailLoading && !req) {
    return (
      <div className="px-3 py-4 text-center text-xs" style={{ color: theme.muted }}>
        Loading line items…
      </div>
    );
  }

  if (!req) return null;

  return (
    <div
      className="request-expanded-content space-y-2"
      style={{
        ['--request-sub-table-bg' as string]: theme.subTableBg,
        ['--request-sub-table-row-hover' as string]: theme.subTableRowHover,
      }}
    >
          {req.notes && (
            <div className="mx-3 text-sm px-3 py-1.5" style={{ color: theme.text }}>
              <span style={{ color: theme.muted }}>Notes: </span>{req.notes}
            </div>
          )}

          <div style={{ borderTop: `1px solid ${theme.border}` }}>
              <table className="w-full table-fixed request-lines-table">
                <colgroup>
                  {lineColumns.map(col => (
                    <col
                      key={col.key}
                      style={{ width: typeof col.width === 'number' ? `${col.width}px` : col.width }}
                    />
                  ))}
                </colgroup>
                <thead style={{ backgroundColor: theme.subTableBg }}>
                  <tr>
                    {showReceivingEdit ? (
                      <>
                        <th className="px-3 text-center">
                          <input
                            type="checkbox"
                            checked={allReceiveLinesSelected}
                            onChange={e => toggleSelectAllReceive(e.target.checked)}
                            title="Select all receivable lines"
                          />
                        </th>
                        {RECEIVING_EDIT_LINE_COLUMNS
                          .filter(col => col.key !== 'include' && (showReceived || col.key !== 'received'))
                          .map(col => (
                          <th
                            key={col.key}
                            className="px-3 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap"
                            style={{ color: theme.muted }}
                          >
                            {col.label}
                          </th>
                        ))}
                      </>
                    ) : showOrderColumns ? (
                      <>
                        {isEditingOrder && (
                          <th className="px-3 text-center">
                            <input
                              type="checkbox"
                              checked={allOrderLinesSelected}
                              onChange={e => toggleSelectAllOrders(e.target.checked)}
                              title="Select all orderable lines"
                            />
                          </th>
                        )}
                        {(isEditingOrder ? ORDERING_LINE_COLUMNS.slice(1) : [
                          ORDERING_VIEW_COLUMN_DEFS.item,
                          ORDERING_VIEW_COLUMN_DEFS.qty,
                          ORDERING_VIEW_COLUMN_DEFS.po,
                          ORDERING_VIEW_COLUMN_DEFS.supplier,
                          ORDERING_VIEW_COLUMN_DEFS.eta,
                        ]).map(col => (
                          <th
                            key={col.key}
                            className="px-3 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap"
                            style={{ color: theme.muted }}
                          >
                            {col.label}
                          </th>
                        ))}
                        {isOrderedView && !isEditingOrder && showReceived && (
                          <th className="px-3 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap" style={{ color: theme.muted }}>
                            Received
                          </th>
                        )}
                      </>
                    ) : (
                      <>
                        <th className="pl-8 pr-3 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap" style={{ color: theme.muted }}>
                          Item
                        </th>
                        <th className="px-3 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap" style={{ color: theme.muted }}>Status</th>
                        <th className="px-3 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap" style={{ color: theme.muted }}>Requested</th>
                        <th className="px-3 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap" style={{ color: theme.muted }}>{canReview ? 'Approve' : 'Approved'}</th>
                        {showReceived && (
                          <th className="px-3 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap" style={{ color: theme.muted }}>Received</th>
                        )}
                        {canReview && (
                          <th className="px-3 text-right text-xs uppercase tracking-wide font-medium whitespace-nowrap" style={{ color: theme.muted }}>Review</th>
                        )}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(req.lines ?? []).map(line => {
                    const d = approvals[line.id];
                    const needsMatch = lineNeedsResolution(line);
                    const item = items.find(i => i.id === effectiveLineItemId(line));
                    const isUnavailableConfirmed = d?.lineDisposition === 'unavailable' && !!d.dispositionConfirmed;
                    const isApproveConfirmed = d?.lineDisposition === 'approve' && !!d.dispositionConfirmed;
                    const isSubstituteConfirmed = d?.lineDisposition === 'substitute' && !!d.dispositionConfirmed;
                    const lineDisposition = d?.lineDisposition ?? null;
                    const dispositionConfirmed = d?.dispositionConfirmed ?? false;
                    const canEditLine = canReview && !['cancelled', 'unavailable'].includes(line.status)
                      && line.substituteStatus !== 'accepted'
                      && line.substituteStatus !== 'pending_manager';
                    const showRequesterNegotiation = !showOrderColumns && !showReceivingEdit && canRequesterReview && line.substituteStatus === 'pending_requester';
                    const showManagerNegotiation = !showOrderColumns && !showReceivingEdit && canReview && canRespondAsManager && line.substituteStatus === 'pending_manager';
                    const qtyReduced = !!d && d.approvedQty < line.requestedQty && !isUnavailableConfirmed;
                    const qtyPendingConfirm = canEditLine && lineDisposition === 'approve' && !dispositionConfirmed && !isUnavailableConfirmed;
                    const editingUnavailable = canEditLine && lineDisposition === 'unavailable' && !dispositionConfirmed;
                    const editingSubstitute = canEditLine && lineDisposition === 'substitute' && !needsMatch && !dispositionConfirmed;
                    const showLineDetail = !showOrderColumns && !showReceivingEdit && (editingUnavailable || editingSubstitute || needsMatch || qtyReduced);
                    const confirmedRowBg = dispositionConfirmed && lineDisposition
                      ? dispoRowBg[lineDisposition]
                      : undefined;
                    const displayStatus = isUnavailableConfirmed
                      ? 'unavailable'
                      : isApproveConfirmed
                        ? 'approved'
                        : isSubstituteConfirmed
                          ? 'substitute'
                          : line.status;
                    const displayLine = isSubstituteConfirmed && d?.substituteItemId
                      ? { ...line, substituteItemId: d.substituteItemId }
                      : line;
                    const orderable = isLineOrderable(line);
                    const lineAlreadyOrdered = LINE_ORDERED_STATUSES.has(line.status);
                    const skipOrder = !!d?.skipOrder;
                    const lineOrderSelected = orderable && !skipOrder;
                    const highlightPo = lineOrderSelected && multiOrderSelected && orderFieldHover === 'po';
                    const highlightSupplier = lineOrderSelected && multiOrderSelected && orderFieldHover === 'supplier';
                    const highlightEta = lineOrderSelected && multiOrderSelected && orderFieldHover === 'eta';
                    const highlightOrdered = lineOrderSelected && multiOrderSelected && orderFieldHover === 'ordered';
                    const savedPo = line.poReference ?? d?.poReference ?? null;
                    const savedSupplier = line.supplier ?? d?.supplier ?? null;
                    const savedEta = line.expectedDate ?? d?.expectedDate ?? null;
                    const receivable = isLineReceivable(line);
                    const rd = receiveDrafts[line.id];
                    const receiveRemaining = receivable
                      ? remainingReceiveQty(line)
                      : 0;
                    const needsSerial = !!item?.requiresSerial;
                    const needsMac = !!item?.requiresMac;
                    const showReceiveSerialRow = showReceivingEdit && receivable && rd?.include !== false && !rd?.dispute
                      && (needsSerial || needsMac) && !skipSerialReceive;
                    const receivingNotOrdered = subTab === 'receiving'
                      && (isOrderedView || showReceivingEdit)
                      && line.status === 'approved';
                    return (
                      <React.Fragment key={line.id}>
                        <tr
                          className="request-line-row"
                          style={{
                            borderTop: `1px solid ${theme.border}`,
                            opacity: isEditingReceive
                              ? ((!receivable || rd?.include === false || rd?.dispute) ? 0.45 : 1)
                              : isEditingOrder
                                ? (!orderable && !lineAlreadyOrdered ? 0.45 : 1)
                                : receivingNotOrdered
                                  ? 0.45
                                  : (isUnavailableConfirmed ? 0.75 : 1),
                            backgroundColor: (showOrderColumns || showReceivingEdit) ? undefined : confirmedRowBg,
                          }}
                        >
                          {showReceivingEdit ? (
                            <>
                              <td className="px-3 align-middle text-center">
                                {receivable ? (
                                  <input
                                    type="checkbox"
                                    checked={rd?.include !== false}
                                    onChange={e => patchReceiveDraft(line.id, { include: e.target.checked })}
                                  />
                                ) : null}
                              </td>
                              <td className="request-line-item-cell px-3 text-sm align-middle min-w-0">
                                <div className="request-line-cell-inner" style={{ color: theme.text }}>
                                  {renderLineItemName(displayLine)}
                                  {item && (needsSerial || needsMac) && receivable && !rd?.dispute && (
                                    <span className={`${lineStatusBadgeClass} flex-shrink-0 ${statusBadge('draft', isDark)}`}>
                                      {needsSerial && needsMac ? 'Serial & MAC' : needsSerial ? 'Serial required' : 'MAC required'}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="request-line-qty-cell px-3 text-sm align-middle">
                                <span className="inline-flex items-center h-5 max-h-5 text-sm" style={{ color: theme.text }}>
                                  {line.status === 'unavailable' || line.status === 'cancelled'
                                    ? '—'
                                    : (line.approvedQty ?? '—')}
                                </span>
                              </td>
                              <td className="px-3 text-sm align-middle min-w-0">{orderReadCell(savedPo)}</td>
                              <td className="px-3 text-sm align-middle min-w-0">{supplierBadgeCell(savedSupplier)}</td>
                              <td className="px-3 text-sm align-middle min-w-0">{orderReadCell(savedEta ? fmtRequestDay(savedEta) : null)}</td>
                              {showReceived && (
                                <td className="request-line-qty-cell px-3 text-sm align-middle" style={{ color: theme.text }}>
                                  {line.receivedQty}
                                </td>
                              )}
                              <td className="request-line-qty-cell px-3 text-sm align-middle min-w-0">
                                {receivable ? (
                                  <div className="request-line-cell-inner">
                                    {!rd?.dispute ? (
                                      <input
                                        type="number"
                                        min={1}
                                        max={receiveRemaining}
                                        value={rd?.qty ?? receiveRemaining}
                                        onChange={e => updateReceiveQty(line.id, parseInt(e.target.value) || 1, line)}
                                        className={`${qtyInputCls} ${qtyHighlightCls}`}
                                        style={qtyFieldStyle(true)}
                                        title={`Max ${receiveRemaining}`}
                                      />
                                    ) : (
                                      <span className="text-xs" style={{ color: '#f87171' }}>Disputed</span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => patchReceiveDraft(line.id, { dispute: !rd?.dispute })}
                                      className="flex items-center justify-center h-5 w-5 rounded transition-colors flex-shrink-0"
                                      style={{
                                        color: rd?.dispute ? '#f43f5e' : theme.muted,
                                        backgroundColor: rd?.dispute ? (isDark ? '#3b1a1a' : '#ffe4e6') : (isDark ? '#2b3140' : '#f3f4f6'),
                                      }}
                                      title={rd?.dispute ? 'Cancel dispute' : 'Flag as wrong item / mismatch'}
                                    >
                                      <Flag size={10} />
                                    </button>
                                  </div>
                                ) : (
                                  <span style={{ color: theme.muted }}>—</span>
                                )}
                              </td>
                            </>
                          ) : showOrderColumns ? (
                            <>
                              {isEditingOrder && (
                              <td className="px-3 align-middle text-center">
                                {orderable ? (
                                  <input
                                    type="checkbox"
                                    checked={!skipOrder}
                                    onChange={e => patchApproval(line.id, { skipOrder: !e.target.checked })}
                                  />
                                ) : null}
                              </td>
                              )}
                              <td className="request-line-item-cell px-3 text-sm align-middle min-w-0">
                                <div className="request-line-cell-inner" style={{ color: theme.text }}>
                                  {renderLineItemName(displayLine)}
                                </div>
                              </td>
                              <td className="request-line-qty-cell px-3 text-sm align-middle">
                                <span className="inline-flex items-center h-5 max-h-5 text-sm" style={{ color: theme.text }}>
                                  {isEditingOrder
                                    ? (orderable || lineAlreadyOrdered ? (line.approvedQty ?? line.requestedQty) : '—')
                                    : (line.status === 'unavailable' || line.status === 'cancelled'
                                      ? '—'
                                      : (line.approvedQty ?? '—'))}
                                </span>
                              </td>
                              <td className="text-sm align-middle min-w-0" style={{ height: '2rem', maxHeight: '2rem', padding: 0 }}>
                                {isEditingOrder && orderable ? (
                                  <div
                                    className="h-8 px-3 flex items-center min-w-0 transition-colors"
                                    style={{ backgroundColor: highlightPo ? orderCellHoverBg : undefined }}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.backgroundColor = orderCellHoverBg;
                                      handleOrderFieldHover('po', lineOrderSelected, true);
                                    }}
                                    onMouseLeave={e => {
                                      handleOrderFieldHover('po', lineOrderSelected, false);
                                      e.currentTarget.style.backgroundColor = '';
                                    }}
                                  >
                                    <input
                                      type="text"
                                      placeholder="PO number"
                                      value={d?.poReference ?? ''}
                                      onChange={e => patchOrderField(line.id, { poReference: e.target.value })}
                                      className="request-order-cell-input"
                                      style={{ color: theme.text }}
                                    />
                                  </div>
                                ) : (
                                  <div className="h-8 px-3 flex items-center min-w-0">
                                    {orderReadCell(savedPo)}
                                  </div>
                                )}
                              </td>
                              <td className="text-sm align-middle min-w-0" style={{ height: '2rem', maxHeight: '2rem', padding: 0 }}>
                                {isEditingOrder && orderable ? (
                                  <div
                                    className="h-8 min-w-0"
                                    style={
                                      supplierErrors.has(line.id)
                                        ? { boxShadow: 'inset 0 0 0 1px #ef4444' }
                                        : undefined
                                    }
                                  >
                                    <SupplierSelect
                                      value={d?.supplier ?? ''}
                                      onChange={supplier => patchOrderField(line.id, { supplier })}
                                      isDark={isDark}
                                      theme={theme}
                                      className="h-8 px-3"
                                      hoverBg={orderCellHoverBg}
                                      forceHover={highlightSupplier}
                                      onHoverChange={hovered => handleOrderFieldHover('supplier', lineOrderSelected, hovered)}
                                    />
                                  </div>
                                ) : (
                                  <div className="h-8 px-3 flex items-center min-w-0">
                                    {supplierBadgeCell(savedSupplier)}
                                  </div>
                                )}
                              </td>
                              <td className="text-sm align-middle min-w-0" style={{ height: '2rem', maxHeight: '2rem', padding: 0 }}>
                                {isEditingOrder && orderable ? (
                                  <ExpectedDateInput
                                    value={d?.expectedDate ?? ''}
                                    onChange={expectedDate => patchOrderField(line.id, { expectedDate })}
                                    className="h-8 px-3"
                                    textColor={theme.text}
                                    mutedColor={theme.muted}
                                    hoverBg={orderCellHoverBg}
                                    forceHover={highlightEta}
                                    onHoverChange={hovered => handleOrderFieldHover('eta', lineOrderSelected, hovered)}
                                  />
                                ) : (
                                  <div className="h-8 px-3 flex items-center min-w-0">
                                    {orderReadCell(savedEta ? fmtRequestDay(savedEta) : null)}
                                  </div>
                                )}
                              </td>
                              {isEditingOrder && (
                                <td className="text-sm align-middle min-w-0" style={{ height: '2rem', maxHeight: '2rem', padding: 0 }}>
                                  {lineAlreadyOrdered ? (
                                    <div className="h-8 px-3 flex items-center justify-center min-w-0">
                                      <Check size={16} style={{ color: '#22c55e' }} aria-label="Ordered" />
                                    </div>
                                  ) : orderable ? (
                                    <button
                                      type="button"
                                      disabled={saving}
                                      onClick={() => void handleConfirmOrdered(line.id)}
                                      className="group h-8 w-full px-3 flex items-center justify-center min-w-0 transition-colors disabled:opacity-50"
                                      style={{
                                        backgroundColor: highlightOrdered ? orderCellHoverBg : undefined,
                                      }}
                                      onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor = orderCellHoverBg;
                                        handleOrderFieldHover('ordered', lineOrderSelected, true);
                                      }}
                                      onMouseLeave={e => {
                                        handleOrderFieldHover('ordered', lineOrderSelected, false);
                                        e.currentTarget.style.backgroundColor = '';
                                      }}
                                      title={
                                        lineOrderSelected && multiOrderSelected
                                          ? `Mark ${selectedOrderLineIds.length} selected lines as ordered`
                                          : 'Mark line as ordered'
                                      }
                                      aria-label={
                                        lineOrderSelected && multiOrderSelected
                                          ? `Mark ${selectedOrderLineIds.length} selected lines as ordered`
                                          : 'Mark line as ordered'
                                      }
                                    >
                                      <Check
                                        size={16}
                                        className={`transition-opacity ${highlightOrdered ? 'opacity-70' : 'opacity-0 group-hover:opacity-70'}`}
                                        style={{ color: '#22c55e' }}
                                      />
                                    </button>
                                  ) : (
                                    <div className="h-8 px-3 flex items-center min-w-0">
                                      <span style={{ color: theme.muted }}>—</span>
                                    </div>
                                  )}
                                </td>
                              )}
                              {isOrderedView && !isEditingOrder && showReceived && (
                                <td className="request-line-qty-cell px-3 text-sm align-middle" style={{ color: theme.text }}>
                                  {line.receivedQty}
                                </td>
                              )}
                            </>
                          ) : (
                            <>
                          <td className="request-line-item-cell pl-8 pr-3 text-sm align-middle">
                            <div className="request-line-cell-inner" style={{ color: theme.text }}>
                                {renderLineItemName(displayLine)}
                                {needsMatch && !isUnavailableConfirmed && (
                                  <span className={`${lineStatusBadgeClass} flex-shrink-0 ${statusBadge('pending_resolution', isDark)}`}>
                                    Awaiting match
                                  </span>
                                )}
                                {line.parentLineId && (
                                  <span className={`${lineStatusBadgeClass} flex-shrink-0 ${statusBadge('draft', isDark)}`}>
                                    Split line
                                  </span>
                                )}
                                {line.substituteStatus === 'pending_manager' && !showManagerNegotiation && (
                                  <span className={`${lineStatusBadgeClass} flex-shrink-0 ${statusBadge('backordered', isDark)}`}>
                                    Awaiting manager
                                  </span>
                                )}
                                {line.substituteItemId && line.substituteStatus === 'declined' && (
                                  <span className={`${lineStatusBadgeClass} flex-shrink-0 ${statusBadge('cancelled', isDark)}`}>
                                    Substitute declined
                                  </span>
                                )}
                                {line.substituteItemId && !line.substituteStatus && !isSubstituteConfirmed && (
                                  <span className={`${lineStatusBadgeClass} flex-shrink-0 ${statusBadge('substitute', isDark)}`}>
                                    Substitute item
                                  </span>
                                )}
                                {item && (item.requiresSerial || item.requiresMac) && !isUnavailableConfirmed && (
                                  <span className={`${lineStatusBadgeClass} flex-shrink-0 ${statusBadge('draft', isDark)}`}>
                                    {item.requiresSerial && item.requiresMac
                                      ? 'Serial & MAC'
                                      : item.requiresSerial
                                        ? 'Serial required'
                                        : 'MAC required'}
                                  </span>
                                )}
                                {!showOrderColumns && !canReview && line.poReference && (
                                  <span className={`${lineStatusBadgeClass} flex-shrink-0 ${statusBadge('draft', isDark)}`}>
                                    PO: {line.poReference}
                                  </span>
                                )}
                                {!showOrderColumns && !canReview && line.supplier && (
                                  <span
                                    className={supplierBadgeClass}
                                    style={supplierBadgeStyle(line.supplier, isDark)}
                                  >
                                    {line.supplier}
                                  </span>
                                )}
                                {!showOrderColumns && !canReview && line.expectedDate && (
                                  <span className={`${lineStatusBadgeClass} flex-shrink-0 ${statusBadge('draft', isDark)}`}>
                                    ETA: {fmtRequestDay(line.expectedDate)}
                                  </span>
                                )}
                            </div>
                          </td>
                          <td className="request-line-status-cell px-3 align-middle overflow-hidden">
                            <span className={`${requestStatusBadgeClass} max-w-full truncate ${statusBadge(displayStatus, isDark)}`}>
                              {isSubstituteConfirmed ? 'Substitute' : fmtStatusLabel(displayStatus)}
                            </span>
                          </td>
                          <td className="request-line-qty-cell px-3 text-sm align-middle">
                            <span className={qtyOutlineCls} style={{ borderColor: theme.border, color: theme.muted }}>
                              {line.requestedQty}
                            </span>
                          </td>
                          <td className="request-line-qty-cell px-3 text-sm align-middle">
                            {canEditLine ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  min={0}
                                  disabled={isUnavailableConfirmed}
                                  value={isUnavailableConfirmed ? 0 : (d?.approvedQty ?? line.requestedQty)}
                                  onChange={e => patchApproval(line.id, { approvedQty: Math.max(0, parseInt(e.target.value) || 0) })}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' && qtyPendingConfirm) confirmApprovedQty(line.id, line);
                                  }}
                                  className={`${qtyInputCls} ${isUnavailableConfirmed ? 'opacity-40 cursor-not-allowed' : ''} ${qtyPendingConfirm ? qtyHighlightCls : ''}`}
                                  style={qtyFieldStyle(qtyPendingConfirm)}
                                  title={qtyPendingConfirm ? 'Adjust qty if needed, then click Approve again or the check beside this field' : undefined}
                                />
                                {qtyPendingConfirm && (
                                  <button
                                    type="button"
                                    title="Approve this qty"
                                    aria-label="Approve this qty"
                                    onClick={() => confirmApprovedQty(line.id, line)}
                                    className={dispoIconBtnCls}
                                    style={{
                                      color: dispoColor.approve,
                                      backgroundColor: dispoActiveBg.approve,
                                    }}
                                  >
                                    <Check size={12} />
                                  </button>
                                )}
                                {isApproveConfirmed && (
                                  <Check size={12} className="flex-shrink-0" style={{ color: dispoColor.approve }} aria-hidden />
                                )}
                              </div>
                            ) : (
                              <span className="inline-flex items-center h-5 max-h-5 text-sm" style={{ color: theme.text }}>
                                {line.status === 'unavailable' ? '—' : (line.approvedQty ?? '—')}
                              </span>
                            )}
                          </td>
                          {showReceived && (
                          <td className="request-line-qty-cell px-3 text-sm align-middle" style={{ color: theme.text }}>{line.receivedQty}</td>
                          )}
                          {canReview && (
                          <td className="request-line-actions-cell px-3 align-middle">
                            {canEditLine ? (
                              <div className="request-line-cell-inner justify-end">
                                  {([
                                    { kind: 'approve' as const, label: 'Approve as requested', icon: <Check size={14} /> },
                                    { kind: 'unavailable' as const, label: 'Mark as unavailable', icon: <Ban size={14} /> },
                                    ...(!needsMatch ? [{ kind: 'substitute' as const, label: 'Propose substitute', icon: <ArrowLeftRight size={14} /> }] : []),
                                  ]).map(({ kind, label, icon }) => {
                                    const active = lineDisposition === kind;
                                    const errored = kind === 'substitute' && substituteErrors.has(line.id);
                                    const restColor = errored ? dispoColor.unavailable : theme.muted;
                                    const approvePending = kind === 'approve' && active && !dispositionConfirmed;
                                    const buttonLabel = approvePending ? 'Approve this qty (click again to confirm)' : label;
                                    return (
                                      <button
                                        key={kind}
                                        type="button"
                                        title={buttonLabel}
                                        aria-label={buttonLabel}
                                        aria-pressed={active}
                                        onClick={() => toggleLineDisposition(line.id, line, kind)}
                                        className={dispoIconBtnCls}
                                        style={{
                                          color: active ? dispoColor[kind] : restColor,
                                          backgroundColor: active
                                            ? (kind === 'approve' && !dispositionConfirmed ? dispoPendingBg.approve : dispoActiveBg[kind])
                                            : 'transparent',
                                        }}
                                        onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.color = theme.text; }}
                                        onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.color = restColor; }}
                                      >
                                        {icon}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </td>
                          )}
                            </>
                          )}
                        </tr>
                        {showReceivingEdit && receivable && rd?.dispute && (
                          <tr style={{ backgroundColor: isDark ? '#2b1e1e' : '#fff1f2' }}>
                            <td colSpan={lineColSpan} className="px-3 py-2 space-y-1">
                              <input
                                type="text"
                                placeholder="Describe the issue (wrong item, damaged, qty mismatch…)"
                                value={rd.disputeReason}
                                onChange={e => patchReceiveDraft(line.id, { disputeReason: e.target.value })}
                                className="w-full text-xs px-2 py-1.5 rounded border request-order-cell-input"
                                style={{ borderColor: '#f43f5e', color: theme.text }}
                              />
                              <p className="text-[10px]" style={{ color: '#f87171' }}>
                                This line will be flagged as disputed and won&apos;t be added to stock.
                              </p>
                            </td>
                          </tr>
                        )}
                        {showReceiveSerialRow && rd && (
                          <tr style={{ backgroundColor: theme.inputBg }}>
                            <td colSpan={lineColSpan} className="px-3 py-2 space-y-2">
                              <p className="text-[10px] uppercase tracking-wide" style={{ color: theme.muted }}>
                                {needsSerial && needsMac ? 'Serial & MAC per unit' : needsSerial ? 'Serial per unit' : 'MAC per unit'}
                                {' · '}{serialReceiveProgress.filled}/{serialReceiveProgress.required} complete
                              </p>
                              {rd.units.map((u, i) => (
                                <div key={i} className="flex flex-wrap gap-2">
                                  {needsSerial && (
                                    <input
                                      type="text"
                                      placeholder={`Unit ${i + 1} serial`}
                                      value={u.serial}
                                      onChange={e => updateReceiveUnit(line.id, i, 'serial', e.target.value)}
                                      className="request-order-cell-input text-xs flex-1 min-w-[140px]"
                                      style={{ borderColor: !u.serial.trim() && needsSerial ? '#f59e0b' : undefined, color: theme.text }}
                                    />
                                  )}
                                  {needsMac && (
                                    <input
                                      type="text"
                                      placeholder={`Unit ${i + 1} MAC`}
                                      value={u.mac}
                                      onChange={e => updateReceiveUnit(line.id, i, 'mac', e.target.value)}
                                      className="request-order-cell-input text-xs flex-1 min-w-[140px]"
                                      style={{ borderColor: !u.mac.trim() && needsMac ? '#f59e0b' : undefined, color: theme.text }}
                                    />
                                  )}
                                </div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {showRequesterNegotiation && (
                          <NegotiationLinePanel
                            line={line}
                            items={items}
                            colSpan={lineColSpan}
                            responderRole="requester"
                            proposerRole="manager"
                            theme={theme}
                            isDark={isDark}
                            inputCls={inputCls}
                            saving={saving}
                            onAccept={() => { setSaving(true); acceptSubstitute(req.id, line.id).finally(() => { setSaving(false); void refreshDetail(); }); }}
                            onDecline={() => { setSaving(true); declineSubstitute(req.id, line.id).finally(() => { setSaving(false); void refreshDetail(); }); }}
                            onCounter={(substituteItemId) => {
                              setSaving(true);
                              counterPropose(req.id, line.id, { substituteItemId, approvedQty: line.approvedQty ?? line.requestedQty })
                                .finally(() => { setSaving(false); void refreshDetail(); });
                            }}
                          />
                        )}
                        {showManagerNegotiation && (
                          <NegotiationLinePanel
                            line={line}
                            items={items}
                            colSpan={lineColSpan}
                            responderRole="manager"
                            proposerRole="requester"
                            theme={theme}
                            isDark={isDark}
                            inputCls={inputCls}
                            saving={saving}
                            onAccept={() => { setSaving(true); acceptSubstitute(req.id, line.id).finally(() => { setSaving(false); void refreshDetail(); }); }}
                            onDecline={() => { setSaving(true); declineSubstitute(req.id, line.id).finally(() => { setSaving(false); void refreshDetail(); }); }}
                            onCounter={(substituteItemId) => {
                              setSaving(true);
                              counterPropose(req.id, line.id, { substituteItemId, approvedQty: line.approvedQty ?? line.requestedQty })
                                .finally(() => { setSaving(false); void refreshDetail(); });
                            }}
                          />
                        )}
                        {showLineDetail && (
                          <tr style={{ backgroundColor: theme.inputBg }}>
                            <td colSpan={lineColSpan} className="pl-8 pr-3 py-2 space-y-2">
                              {editingUnavailable && (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    placeholder="Reason unavailable…"
                                    value={d?.unavailableReason ?? ''}
                                    onChange={e => {
                                      patchApproval(line.id, { unavailableReason: e.target.value });
                                      if (messageIsError) clearMessage();
                                    }}
                                    onKeyDown={e => { if (e.key === 'Enter') confirmLineDisposition(line.id, line); }}
                                    className={`${dispositionFieldClass} flex-1 min-w-0`}
                                    style={dispositionFieldStyle}
                                    autoFocus
                                  />
                                  <button
                                    type="button"
                                    onClick={() => confirmLineDisposition(line.id, line)}
                                    className={detailBtnPrimaryCls}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => resetLineDisposition(line.id, line)}
                                    className={detailBtnSecondaryCls}
                                    style={{ backgroundColor: isDark ? '#2b3140' : '#f3f4f6', color: theme.muted }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                              {editingSubstitute && (
                                <div className="flex items-center gap-2">
                                  <select
                                    value={d?.substituteItemId ?? ''}
                                    onChange={e => {
                                      patchApproval(line.id, { substituteItemId: e.target.value });
                                      if (e.target.value) {
                                        setSubstituteErrors(prev => {
                                          if (!prev.has(line.id)) return prev;
                                          const next = new Set(prev);
                                          next.delete(line.id);
                                          return next;
                                        });
                                      }
                                    }}
                                    className={`${dispositionFieldClass} flex-1 min-w-0`}
                                    style={{
                                      ...dispositionFieldStyle,
                                      borderColor: substituteErrors.has(line.id) ? '#ef4444' : theme.border,
                                    }}
                                    aria-invalid={substituteErrors.has(line.id)}
                                    autoFocus
                                  >
                                    <option value="">Select substitute…</option>
                                    {items
                                      .filter(i => i.id !== (line.itemId ?? d?.matchItemId))
                                      .map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => confirmLineDisposition(line.id, line)}
                                    className={detailBtnPrimaryCls}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => resetLineDisposition(line.id, line)}
                                    className={detailBtnSecondaryCls}
                                    style={{ backgroundColor: isDark ? '#2b3140' : '#f3f4f6', color: theme.muted }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                              {!editingUnavailable && !editingSubstitute && lineDisposition !== 'unavailable' && needsMatch && (() => {
                                const qc = quickCreate[line.id];
                                return (
                                  <div className="space-y-1.5">
                                    <label className="text-[10px] uppercase tracking-wide block" style={{ color: theme.muted }}>Match to catalogue item</label>
                                    {!qc ? (
                                      <div className="flex gap-2">
                                        <select
                                          value={d?.matchItemId ?? ''}
                                          onChange={e => {
                                            const matchItemId = e.target.value;
                                            patchApproval(line.id, { matchItemId });
                                            if (matchItemId) {
                                              void persistLineReview(line, { ...(d ?? approvalDraftFromLine(line)), matchItemId }, { matchOnly: true });
                                            }
                                          }}
                                          className={inputCls + ' flex-1'}
                                          style={{ backgroundColor: theme.cardBg, borderColor: theme.border, color: theme.text }}
                                        >
                                          <option value="">Select item…</option>
                                          {cataloguePickerItems(items, d?.matchItemId).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                                        </select>
                                        <button
                                          onClick={() => patchQC(line.id, { name: line.freeTextDescription ?? '' })}
                                          className="flex-shrink-0 flex items-center gap-1 text-[11px] px-2 py-1 rounded transition-colors"
                                          style={{ color: '#3b82f6', backgroundColor: isDark ? '#1d2535' : '#eff6ff' }}
                                          title="Create new catalogue item"
                                        >
                                          <Plus size={11} />
                                          New item
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="rounded p-2 space-y-1.5" style={{ border: `1px solid ${theme.border}`, backgroundColor: theme.cardBg }}>
                                        <p className="text-[11px] font-medium" style={{ color: theme.text }}>New catalogue item</p>
                                        <input
                                          type="text"
                                          placeholder="Item name…"
                                          value={qc.name}
                                          onChange={e => patchQC(line.id, { name: e.target.value })}
                                          className={inputCls}
                                          style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                                        />
                                        <input
                                          type="text"
                                          placeholder="Category (optional)…"
                                          value={qc.category}
                                          onChange={e => patchQC(line.id, { category: e.target.value })}
                                          className={inputCls}
                                          style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                                        />
                                        <div className="flex gap-4">
                                          <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: theme.muted }}>
                                            <input type="checkbox" checked={qc.requiresSerial} onChange={e => patchQC(line.id, { requiresSerial: e.target.checked })} />
                                            Requires serial
                                          </label>
                                          <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: theme.muted }}>
                                            <input type="checkbox" checked={qc.requiresMac} onChange={e => patchQC(line.id, { requiresMac: e.target.checked })} />
                                            Requires MAC
                                          </label>
                                        </div>
                                        <div className="flex gap-2">
                                          <button
                                            disabled={!qc.name.trim() || qc.saving}
                                            onClick={async () => {
                                              patchQC(line.id, { saving: true });
                                              const newItem = await useInventoryStore.getState().addItem({
                                                name: qc.name.trim(),
                                                sku: null,
                                                category: qc.category.trim() || null,
                                                notes: null,
                                                requiresSerial: qc.requiresSerial,
                                                requiresMac: qc.requiresMac,
                                              });
                                              if (newItem) {
                                                patchApproval(line.id, { matchItemId: newItem.id });
                                                setQuickCreate(prev => { const n = { ...prev }; delete n[line.id]; return n; });
                                                void persistLineReview(line, { ...(d ?? approvalDraftFromLine(line)), matchItemId: newItem.id }, { matchOnly: true });
                                              } else {
                                                patchQC(line.id, { saving: false });
                                              }
                                            }}
                                            className="text-[11px] px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
                                          >
                                            {qc.saving ? 'Creating…' : 'Create & select'}
                                          </button>
                                          <button
                                            onClick={() => setQuickCreate(prev => { const n = { ...prev }; delete n[line.id]; return n; })}
                                            className="text-[11px] px-2 py-1 rounded transition-colors"
                                            style={{ backgroundColor: isDark ? '#2b3140' : '#f3f4f6', color: theme.muted }}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {lineDisposition !== 'unavailable' && d && d.approvedQty < line.requestedQty && (
                                <>
                                  <input
                                    type="text"
                                    placeholder="Reason for reduced qty…"
                                    value={d.approvalReason}
                                    onChange={e => patchApproval(line.id, { approvalReason: e.target.value })}
                                    className={inputCls}
                                    style={{ backgroundColor: theme.cardBg, borderColor: theme.border, color: theme.text }}
                                  />
                                  {(() => {
                                    const remainder = line.requestedQty - (d.approvedQty ?? line.requestedQty);
                                    const sd = splitDrafts[line.id];
                                    if (remainder <= 0) return null;
                                    return sd ? (
                                      <div className="rounded p-2 space-y-1.5" style={{ border: `1px solid ${theme.border}`, backgroundColor: theme.cardBg }}>
                                        <p className="text-[11px] font-medium" style={{ color: theme.text }}>Split {remainder} remaining unit(s) to new line</p>
                                        <div className="flex items-center gap-2">
                                          <label className="text-[10px] flex-shrink-0" style={{ color: theme.muted }}>Qty to split</label>
                                          <input
                                            type="number"
                                            min={1}
                                            max={remainder}
                                            value={sd.qty}
                                            onChange={e => patchSplit(line.id, { qty: Math.min(remainder, Math.max(1, parseInt(e.target.value) || 1)) })}
                                            className={`${qtyInputCls} w-16`}
                                            style={qtyInputStyle}
                                          />
                                        </div>
                                        {!needsMatch && (
                                          <div>
                                            <label className="text-[10px] block mb-0.5" style={{ color: theme.muted }}>Different SKU (optional)</label>
                                            <select
                                              value={sd.itemId}
                                              onChange={e => patchSplit(line.id, { itemId: e.target.value })}
                                              className={inputCls}
                                              style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: sd.itemId ? theme.text : theme.muted }}
                                            >
                                              <option value="">Same item</option>
                                              {cataloguePickerItems(items, sd.itemId).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                                            </select>
                                          </div>
                                        )}
                                        <div className="flex gap-2">
                                          <button
                                            disabled={sd.saving}
                                            onClick={async () => {
                                              patchSplit(line.id, { saving: true });
                                              const ok = await splitLine(req.id, line.id, sd.qty, sd.itemId || null);
                                              if (ok) {
                                                setSplitDrafts(prev => { const n = { ...prev }; delete n[line.id]; return n; });
                                                await refreshDetail();
                                              } else {
                                                patchSplit(line.id, { saving: false });
                                              }
                                            }}
                                            className="text-[11px] px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
                                          >
                                            {sd.saving ? 'Splitting…' : 'Create split line'}
                                          </button>
                                          <button
                                            onClick={() => setSplitDrafts(prev => { const n = { ...prev }; delete n[line.id]; return n; })}
                                            className="text-[11px] px-2 py-1 rounded transition-colors"
                                            style={{ backgroundColor: isDark ? '#2b3140' : '#f3f4f6', color: theme.muted }}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => patchSplit(line.id, { qty: remainder })}
                                        className="text-[10px] px-2 py-0.5 rounded transition-colors self-start"
                                        style={{ color: '#3b82f6', backgroundColor: isDark ? '#1d2535' : '#eff6ff' }}
                                      >
                                        Split {remainder} remaining to new line
                                      </button>
                                    );
                                  })()}
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                        {canReview && !showOrderColumns && line.status === 'disputed' && (
                          <tr style={{ backgroundColor: isDark ? '#2b1e1e' : '#fff1f2' }}>
                            <td colSpan={lineColSpan} className="px-3 py-2 space-y-2">
                              <div className="text-xs font-medium" style={{ color: '#f87171' }}>
                                Disputed — {line.disputeReason || 'Wrong item / mismatch'}
                              </div>
                              <p className="text-[11px]" style={{ color: theme.muted }}>How do you want to resolve this?</p>
                              <div className="flex gap-2 flex-wrap">
                                <button
                                  onClick={() => { void resolveDispute(req.id, line.id, 'accept').then(() => refreshDetail()); }}
                                  className="text-[11px] px-2 py-1 rounded bg-green-600 hover:bg-green-700 text-white transition-colors"
                                >
                                  Accept as received
                                </button>
                                <button
                                  onClick={() => { void resolveDispute(req.id, line.id, 'reorder').then(() => refreshDetail()); }}
                                  className="text-[11px] px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                                >
                                  Re-order
                                </button>
                                <button
                                  onClick={() => { void resolveDispute(req.id, line.id, 'cancel').then(() => refreshDetail()); }}
                                  className="text-[11px] px-2 py-1 rounded transition-colors"
                                  style={{ backgroundColor: isDark ? '#2b3140' : '#e5e7eb', color: theme.muted }}
                                >
                                  Cancel line
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                        {canReview && !showOrderColumns && !canEditLine && (line.status === 'approved' || line.status === 'ordered' || line.status === 'backordered') && (() => {
                          const la = lineActions[line.id];
                          if (!la) return (
                            <tr>
                              <td colSpan={lineColSpan} className="px-2 pb-2 pt-0">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => patchLineAction(line.id, { type: 'cancel', reason: '', expectedDate: '' })}
                                    className="text-[10px] px-2 py-0.5 rounded transition-colors"
                                    style={{ color: '#ef4444', backgroundColor: isDark ? '#2b1e1e' : '#fee2e2' }}
                                  >
                                    Cancel line
                                  </button>
                                  <button
                                    onClick={() => patchLineAction(line.id, { type: 'backorder', reason: '', expectedDate: '' })}
                                    className="text-[10px] px-2 py-0.5 rounded transition-colors"
                                    style={{ color: theme.muted, backgroundColor: isDark ? '#2b3140' : '#e5e7eb' }}
                                  >
                                    Backorder / defer
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                          return (
                            <tr style={{ backgroundColor: theme.inputBg }}>
                              <td colSpan={lineColSpan} className="px-2 py-2 space-y-2">
                                <p className="text-[11px] font-medium" style={{ color: la.type === 'cancel' ? '#ef4444' : theme.muted }}>
                                  {la.type === 'cancel' ? 'Cancel this line' : 'Backorder / defer'}
                                </p>
                                <input
                                  type="text"
                                  placeholder={la.type === 'cancel' ? 'Reason for cancellation…' : 'Note (e.g. supplier delay)…'}
                                  value={la.reason}
                                  onChange={e => patchLineAction(line.id, { reason: e.target.value })}
                                  className={inputCls}
                                  style={{ backgroundColor: theme.cardBg, borderColor: theme.border, color: theme.text }}
                                />
                                {la.type === 'backorder' && (
                                  <ExpectedDateInput
                                    value={la.expectedDate}
                                    onChange={expectedDate => patchLineAction(line.id, { expectedDate })}
                                    compact={false}
                                    className={`${inputCls} px-2 py-1.5`}
                                    textColor={theme.text}
                                    mutedColor={theme.muted}
                                    hoverBg={isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'}
                                    style={{
                                      backgroundColor: theme.cardBg,
                                      borderColor: theme.border,
                                      color: la.expectedDate ? theme.text : theme.muted,
                                    }}
                                  />
                                )}
                                <div className="flex gap-2">
                                  <button
                                    onClick={async () => {
                                      const ok = la.type === 'cancel'
                                        ? await cancelLine(req.id, line.id, la.reason)
                                        : await backorderLine(req.id, line.id, la.reason, la.expectedDate || undefined);
                                      if (ok) {
                                        setLineActions(prev => { const n = { ...prev }; delete n[line.id]; return n; });
                                        await refreshDetail();
                                      }
                                    }}
                                    className="text-[11px] px-2 py-1 rounded text-white transition-colors"
                                    style={{ backgroundColor: la.type === 'cancel' ? '#ef4444' : '#3b82f6' }}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => setLineActions(prev => { const n = { ...prev }; delete n[line.id]; return n; })}
                                    className="text-[11px] px-2 py-1 rounded transition-colors"
                                    style={{ backgroundColor: isDark ? '#2b3140' : '#f3f4f6', color: theme.muted }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              <div className="px-3 py-1.5 request-lines-footer" style={{ borderTop: `1px solid ${theme.border}` }}>
                <div className="flex items-center gap-2 min-h-5">
                  {(req.events ?? []).length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowActivity(v => !v)}
                      className="inline-flex items-center gap-1 h-5 flex-shrink-0 text-xs italic tracking-wide transition-colors"
                      style={{ color: activityLabelColor }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = theme.text; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = activityLabelColor; }}
                    >
                      {showActivity ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      Activity
                    </button>
                  )}

                  <div className="flex flex-wrap items-center justify-end gap-1.5 ml-auto">
                    {req.status === 'submitted' && (
                      <button
                        type="button"
                        onClick={handleCancel}
                        className={footerActionBtnCls}
                        style={footerSecondaryBtnStyle}
                      >
                        Cancel Request
                      </button>
                    )}
                    {canReview && req.status === 'submitted' && (
                      <button
                        type="button"
                        disabled={saving || hasManagerCounters || !allReviewLinesActioned}
                        onClick={handleApprove}
                        className={
                          !saving && !hasManagerCounters && allReviewLinesActioned
                            ? `${footerActionBtnCls} bg-green-600 hover:bg-green-700 text-white`
                            : footerActionBtnCls
                        }
                        style={
                          !saving && !hasManagerCounters && allReviewLinesActioned
                            ? undefined
                            : footerSecondaryBtnStyle
                        }
                        title={
                          hasManagerCounters
                            ? 'Respond to counter-proposals above first'
                            : !allReviewLinesActioned
                              ? 'Review each line item before approving the request'
                              : undefined
                        }
                      >
                        {saving ? 'Saving…' : draftNeedsRequesterReview ? 'Send for review' : 'Approve Request'}
                      </button>
                    )}
                    {canReceive && receivableLines.length > 0 && !showReceiveForm && (
                      <button
                        type="button"
                        onClick={startReceiveForm}
                        className={`${footerActionBtnCls} bg-green-600 hover:bg-green-700 text-white`}
                      >
                        Receive stock
                      </button>
                    )}
                    {showReceiveForm && (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowReceiveForm(false)}
                          className={footerActionBtnCls}
                          style={footerSecondaryBtnStyle}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={saving || noReceiveLinesSelected || !canConfirmReceive}
                          onClick={handleConfirmReceive}
                          className={`${footerActionBtnCls} bg-green-600 hover:bg-green-700 text-white disabled:opacity-50`}
                        >
                          {saving ? 'Adding…' : 'Confirm & add to stock'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {showReceiveForm && serialReceiveProgress.required > 0 && (
                  <div className="space-y-2 mt-2 pt-2" style={{ borderTop: `1px solid ${theme.border}` }}>
                    <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: theme.muted }}>
                      <input
                        type="checkbox"
                        checked={skipSerialReceive}
                        onChange={e => setSkipSerialReceive(e.target.checked)}
                      />
                      Receive without serials/MACs (requires reason)
                    </label>
                    {skipSerialReceive && (
                      <input
                        type="text"
                        placeholder="Reason…"
                        value={skipSerialReason}
                        onChange={e => setSkipSerialReason(e.target.value)}
                        className="w-full text-xs px-2 py-1.5 rounded border request-order-cell-input"
                        style={{ color: theme.text }}
                      />
                    )}
                    {!canConfirmReceive && !skipSerialReceive && (
                      <div className="flex items-start gap-2 text-xs text-amber-500">
                        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                        <span>Enter all required serial numbers and MAC addresses before adding to stock.</span>
                      </div>
                    )}
                  </div>
                )}

                {showActivity && (req.events ?? []).length > 0 && (
                  <div className="space-y-2 mt-2 pt-2" style={{ borderTop: `1px solid ${theme.border}` }}>
                    {(req.events ?? []).map(ev => (
                      <div key={ev.id} className="text-xs flex gap-2">
                        <span className="whitespace-nowrap shrink-0" style={{ color: theme.muted }}>{fmtRequestDate(ev.createdAt)}</span>
                        <span style={{ color: theme.text }}>
                          <span className="font-medium">{ev.userDisplayName}</span>
                          {' · '}{formatActivityDescription(ev)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
          </div>

          {message && <p className={`px-3 text-xs ${messageIsError ? 'text-red-400' : 'text-green-500'}`}>{message}</p>}

    </div>
  );
};

export default RequestExpandedPanel;
